// crewbit-admin/src/lib/errorMapSite.ts
//
// Phase F (§T.115) — Site module error codes + per-RPC copy + defaults.
// Mirrors crewbit-mobile/lib/errorMessagesSite.ts.
// Split out of errorMap.ts per GR-14 (500 LoC max).

export type SiteErrorCode =
  | 'SLUG_TAKEN'
  | 'SLUG_RESERVED'
  | 'SLUG_INVALID_FORMAT'
  | 'SIGNATURE_INVALID'
  | 'SIGNATURE_HASH_MISMATCH'
  | 'QUOTE_NOT_SIGNABLE'
  | 'CHANGE_ORDER_NOT_SIGNABLE'
  | 'SITE_ACCESS_DENIED'
  | 'SITE_FEATURE_DISABLED'
  | 'QUOTE_CANCELLED'
  | 'CHANGE_ORDER_CANCELLED'
  | 'QUOTE_NOT_CANCELLABLE'
  | 'CHANGE_ORDER_NOT_CANCELLABLE'
  | 'INVOICE_NOT_PAYABLE'
  | 'QUOTE_NOT_SIGNED'
  | 'TOKEN_NOT_FOUND'
  | 'NOT_AUTHORIZED';

export const siteErrorNamespaces: Record<string, Partial<Record<SiteErrorCode | 'FORBIDDEN' | 'NO_ACTIVE_BUSINESS' | 'QUOTE_NOT_FOUND' | 'CHANGE_ORDER_NOT_FOUND', string>>> = {
  cancel_quote: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then cancel quotes.",
    FORBIDDEN: "Only owners and managers can cancel quotes.",
    QUOTE_NOT_FOUND: "That quote no longer exists.",
    QUOTE_NOT_CANCELLABLE:
      "This quote can't be cancelled in its current state. Approved quotes are immutable — use a change order to amend.",
  },
  cancel_change_order: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then cancel change orders.",
    FORBIDDEN: "Only owners and managers can cancel change orders.",
    CHANGE_ORDER_NOT_FOUND: "That change order no longer exists.",
    CHANGE_ORDER_NOT_CANCELLABLE:
      "This change order can't be cancelled in its current state. Approved COs are immutable.",
  },
  sign_quote_by_token: {
    SIGNATURE_INVALID:
      "Couldn't read that signature. Try again with a typed name or a clear drawn signature.",
    TOKEN_NOT_FOUND: "This signing link isn't valid. Contact the business for an updated link.",
    SITE_ACCESS_DENIED: "This isn't available online. Contact the business directly.",
    QUOTE_CANCELLED: "This quote was cancelled and can't be signed.",
    QUOTE_NOT_SIGNABLE: "This quote can't be signed in its current state.",
  },
  sign_change_order_by_token: {
    SIGNATURE_INVALID: "Couldn't read that signature. Try again.",
    TOKEN_NOT_FOUND: "This signing link isn't valid.",
    SITE_ACCESS_DENIED: "This isn't available online. Contact the business directly.",
    CHANGE_ORDER_CANCELLED: "This change order was cancelled.",
    CHANGE_ORDER_NOT_SIGNABLE: "This change order can't be signed in its current state.",
  },
  update_business_slug: {
    NOT_AUTHORIZED: "You don't have permission to change this portal address.",
    SLUG_TAKEN: "That portal address is already taken. Try a different name.",
    SLUG_RESERVED: "That portal address is reserved. Choose another.",
    SLUG_INVALID_FORMAT:
      "Portal addresses use lowercase letters, numbers, and dashes (3–40 characters).",
  },
};

/**
 * Phase F §3.2.0 envelope helpers: normalize an RPC response's error to a stable
 * code regardless of envelope shape (legacy flat string OR Phase F nested {code, meta}).
 */
export function extractErrorCode(envelope: unknown): string {
  if (envelope == null || typeof envelope !== 'object') return 'UNKNOWN';
  const err = (envelope as { error?: unknown }).error;
  if (typeof err === 'string') return err.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
  if (err && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string') {
    return (err as { code: string }).code;
  }
  return 'UNKNOWN';
}

export function extractErrorMeta(envelope: unknown): Record<string, unknown> | null {
  if (envelope == null || typeof envelope !== 'object') return null;
  const err = (envelope as { error?: unknown }).error;
  if (err && typeof err === 'object' && (err as { meta?: unknown }).meta != null
      && typeof (err as { meta: unknown }).meta === 'object') {
    return (err as { meta: Record<string, unknown> }).meta;
  }
  return null;
}

export const siteDefaultErrorMessages: Record<SiteErrorCode, string> = {
  SLUG_TAKEN: "That portal address is already taken.",
  SLUG_RESERVED: "That portal address is reserved.",
  SLUG_INVALID_FORMAT: "Use lowercase letters, numbers, and dashes (3–40 characters).",
  SIGNATURE_INVALID: "Couldn't read that signature. Try again.",
  SIGNATURE_HASH_MISMATCH: "The document changed since you started. Reload and try again.",
  QUOTE_NOT_SIGNABLE: "This quote can't be signed in its current state.",
  CHANGE_ORDER_NOT_SIGNABLE: "This change order can't be signed in its current state.",
  SITE_ACCESS_DENIED: "This isn't available online. Contact the business directly.",
  SITE_FEATURE_DISABLED: "This feature isn't available right now.",
  QUOTE_CANCELLED: "This quote was cancelled.",
  CHANGE_ORDER_CANCELLED: "This change order was cancelled.",
  QUOTE_NOT_CANCELLABLE: "This quote can't be cancelled in its current state.",
  CHANGE_ORDER_NOT_CANCELLABLE: "This change order can't be cancelled in its current state.",
  INVOICE_NOT_PAYABLE: "This invoice can't be paid in its current state.",
  QUOTE_NOT_SIGNED: "This quote needs to be signed before a deposit can be paid.",
  TOKEN_NOT_FOUND: "That link isn't valid.",
  NOT_AUTHORIZED: "You don't have permission to do this.",
};
